<template>
  <RevTable :collection="marketShippingMethod" :columns transparent-header>
    <template #body-category="{ item }">
      {{
        item.category?.name
          ? item.category?.name
          : marketShippingMethod.some(
                (method: MarketShippingMethod) => method.category?.name,
              )
            ? i18n(translations.otherCategories)
            : i18n(translations.allCategories)
      }}
    </template>
    <template #body-carrier="{ item }">
      {{ item.carrier?.name || '-' }}
    </template>
    <template #body-delay="{ item }">
      {{
        item.shippingDelay
          ? i18n(translations.shippingDelayValueInHour, {
              number: item.shippingDelay,
            })
          : '-'
      }}
    </template>
    <template #body-cost="{ item }">
      {{
        parseInt(item.cost || '0', 10) === 0
          ? i18n(translations.free)
          : getMarketPrice(item.cost, item.market)
      }}
    </template>
    <template #body-actions="{ item }">
      <RevButton size="small" variant="secondary" @click="handleEdit(item)">
        {{ i18n(translations.edit) }}
      </RevButton>

      <DeleteShippingMethodModal
        :marketShippingMethod="item"
        @deleted="handleDeleted"
      />

      <RevButton
        class="ml-4"
        size="small"
        variant="secondary"
        @click="handleDelete(item)"
      >
        {{ i18n(translations.delete) }}
      </RevButton>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButton } from '@ds/components/Button'
import { openModal } from '@ds/components/ModalBase'
import { RevTable } from '@ds/components/Table'

import type {
  MarketShippingMethod,
  MarketShippingMethods,
} from '../../ShippingLegacy.types'
import { MODAL_NAME as DELETE_MODAL } from '../DeleteShippingMethodModal/DeleteShippingMethodModal.constants'
import DeleteShippingMethodModal from '../DeleteShippingMethodModal/DeleteShippingMethodModal.vue'

import translations from './ShippingTable.translations'

const emit = defineEmits(['edit', 'deleted'])
defineProps<{ marketShippingMethod: MarketShippingMethods }>()

const { allMarkets } = useMarketplace()

const i18n = useI18n()

const columns = [
  { key: 'category', label: i18n(translations.category) },
  { key: 'carrier', label: i18n(translations.carrier) },
  {
    key: 'shippingDelay',
    label: i18n(translations.shippingDelay),
    isTabular: true,
  },
  { key: 'cost', label: i18n(translations.cost), isTabular: true },
  { key: 'actions', label: '', align: 'end' as const },
]

async function handleDelete(shippingMethod: MarketShippingMethod) {
  openModal(DELETE_MODAL + shippingMethod.id)
}

function handleDeleted() {
  emit('deleted')
}

function handleEdit(shippingMethod: MarketShippingMethod) {
  emit('edit', shippingMethod)
}

function getMarketPrice(amount: string, market: MarketCountryCode) {
  const marketCurrency = allMarkets[market]?.defaultCurrency || ''

  return marketCurrency
    ? i18n.price(amount, { currency: marketCurrency })
    : i18n.price(amount)
}
</script>
